import { getInitials } from "@storytell/sutils";
import type { ColorVariant } from "@storytell/types";
import { useNativeTooltipTitle } from "@storytell/ui/tooltip";
import { type Component, createEffect, createSignal, Show } from "solid-js";
import { getColorVariantForText } from "~/components/utils/colors";
import { useWire } from "~/wire";

// AvatarSize defines the size of the avatar, which can be a predefined value
// or a custom pixel size.
type AvatarSize = "2xs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | number;

type AvatarProps = {
  // Image source URL
  src?: string;

  // Name to display and generate initials from
  name?: string;

  // Size in predefined values or pixels
  size?: AvatarSize;

  // Additional styling
  class?: string;
  classList?: { [key: string]: boolean };
};

const sizeClasses: Record<Exclude<AvatarSize, number>, string> = {
  "2xs": "size-4 text-[10px]",
  xs: "size-6 text-xs",
  sm: "size-8 text-sm",
  md: "size-12 text-base",
  lg: "size-16 text-lg",
  xl: "size-24 text-xl",
  "2xl": "size-32 text-2xl",
};

// Avatar-specific color variants with their RGB values for contrast calculation
const avatarColorVariants: ColorVariant[] = [
  { bg: "bg-purple-400/10", text: "text-purple-400", ring: "ring-purple-400/30", rgb: [147, 51, 234] },
  { bg: "bg-blue-400/10", text: "text-blue-400", ring: "ring-blue-400/30", rgb: [59, 130, 246] },
  { bg: "bg-green-400/10", text: "text-green-400", ring: "ring-green-400/30", rgb: [34, 197, 94] },
  { bg: "bg-yellow-400/10", text: "text-yellow-400", ring: "ring-yellow-400/30", rgb: [234, 179, 8] },
  { bg: "bg-red-400/10", text: "text-red-400", ring: "ring-red-400/30", rgb: [239, 68, 68] },
  { bg: "bg-indigo-400/10", text: "text-indigo-400", ring: "ring-indigo-400/30", rgb: [99, 102, 241] },
  { bg: "bg-pink-400/10", text: "text-pink-400", ring: "ring-pink-400/30", rgb: [236, 72, 153] },
  { bg: "bg-cyan-400/10", text: "text-cyan-400", ring: "ring-cyan-400/30", rgb: [6, 182, 212] },
];

const defaultSrc = "/avatar/guest-avatar.png";
export const Avatar: Component<AvatarProps> = (props) => {
  const wire = useWire();
  const nativeTitle = useNativeTooltipTitle();
  // If this is a guest user, use the default avatar so we don't show
  // "GU" as initials.
  const src = () => (wire.auth.isGuest() ? defaultSrc : props.src);
  const [imgError, setImgError] = createSignal(false);
  createEffect(() => {
    src();
    setImgError(false);
  });
  const name = () => props.name || wire.auth.user()?.displayName || "Guest User";
  const size = () => props.size || "md";

  const sizeClass = () => {
    const s = size();
    if (typeof s === "number") {
      return `size-[${s}px] text-[${s * 0.4}px]`;
    }
    return sizeClasses[s];
  };

  const colors = () => {
    return getColorVariantForText(name(), avatarColorVariants);
  };

  const classList = () => ({
    [colors().bg]: !!colors().bg,
    [colors().ring]: !!colors().ring,
    [colors().text]: !!colors().text,
    [sizeClass()]: !!sizeClass(),
    ...(props.classList || {}),
    ...(props.class ? { [props.class]: true } : {}),
  });

  return (
    <span
      class="relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-canvas ring-1 ring-inset"
      classList={classList()}
    >
      <Show
        when={src() && !imgError()}
        fallback={<span class={`font-medium ${colors().text}`}>{getInitials(name())}</span>}
      >
        <img
          src={src()}
          alt={name()}
          title={nativeTitle(name())}
          class="absolute inset-0 size-full object-cover"
          onError={() => setImgError(true)}
        />
      </Show>
    </span>
  );
};
